/**
 * hearingWords.js — every word the hearing-outcome flow says, in ONE place (docs/HEARING_OUTCOMES.md).
 *
 * Pure. Imported by the Permitting Coordinator's card (TriageGate → HearingOutcomeCard.jsx, for the
 * drafts the coordinator reads and edits), by the Client Hub card (HearingApprovalEntry.jsx, for
 * "what happens next"), and by the server (api/_hearings.js, for the Slack post and the permit log),
 * so the card, the email, the Hub and Slack can never tell the story differently.
 *
 * Voice: the firm's own (brand/alder-james-brand-profile.md "Voice & Tone"): restrained, specific,
 * confident. Celebration is allowed and meant; spectacle is not. The vote is a detail worth sharing;
 * what a vote means for an appeal is NOT stated to the client (it reads as a legal claim; Matt,
 * 2026-10-23). Every jurisdiction runs hearings differently, so nothing here names Laguna's rules.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const NUM = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

function parse(ymd) { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || '')); return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)) : null }
/** "October 22" */
export function fmtDate(ymd) { const d = parse(ymd); return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` : '' }
/** "Thursday, October 22" */
export function fmtDayDate(ymd) { const d = parse(ymd); return d ? `${DAYS[d.getUTCDay()]}, ${fmtDate(ymd)}` : '' }
export function addDays(ymd, n) { const d = parse(ymd); if (!d) return null; d.setUTCDate(d.getUTCDate() + Number(n || 0)); return d.toISOString().slice(0, 10) }
const word = (n) => NUM[n] || String(n)
const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || ''

export const OUTCOMES = {
  approved: 'Approved',
  approved_conditions: 'Approved with conditions',
  continued: 'Continued',
  denied: 'Denied',
}
export const isApproval = (o) => o === 'approved' || o === 'approved_conditions'

/** "5-0", "4-1", "4-0 with one member absent". Empty when no vote was recorded. */
export function voteLine(v) {
  if (!v || v.for == null || v.for === '') return ''
  const f = Number(v.for), a = Number(v.against || 0), ab = Number(v.absent || 0)
  return `${f}-${a}` + (ab > 0 ? ` with ${word(ab)} member${ab === 1 ? '' : 's'} absent` : '')
}

/** The city from a project address ("3192 Alta Laguna Blvd\nLaguna Beach, California 92651" → "Laguna Beach"). */
export function cityOf(address) {
  const lines = String(address || '').split('\n').map(x => x.trim()).filter(Boolean)
  const last = lines.length > 1 ? lines[lines.length - 1] : ''
  return last.includes(',') ? last.split(',')[0].trim() : ''
}

/** The appeal period ends this many days after the hearing (calendar days, as the body runs it). */
export function appealEndsOn(hearingDate, appealDays) {
  return appealDays == null || appealDays === '' ? null : addDays(hearingDate, Number(appealDays))
}

/** The Client Hub card's "What happens next", and the same lines in the client email. */
export function nextSteps({ outcome, appealEnds, storyPoles, conditions, appealStatus }) {
  const out = []
  if (!isApproval(outcome)) return out
  if (appealStatus === 'none') {
    out.push('The appeal period has ended with no appeals filed. The approval is final.')
    if (storyPoles) out.push('The story poles can come down now. The staking company can schedule their removal.')
  } else if (appealStatus === 'filed') {
    out.push('An appeal was filed during the appeal period. We will walk you through what it means and what happens next.')
  } else if (appealEnds) {
    out.push(`The decision can be appealed until ${fmtDate(appealEnds)}. We will confirm with you once that date has passed.`)
    if (storyPoles) out.push(`The story poles need to stay up until then. They can come down after ${fmtDate(appealEnds)}.`)
  }
  if (conditions && conditions.length) out.push('We are folding the board’s conditions into the design as we carry it forward, and they will travel with the plans when we submit for the building permit.')
  out.push('Next, we carry the approved design into the drawings for the building permit.')
  return out
}

/** The permit log memo. It shows on the client's Permitting card (memos are written for the client). */
export function memoDraft({ outcome, bodyName, hearingDate, vote, conditions, appealEnds, continuedTo }) {
  const b = bodyName ? 'The ' + bodyName : 'The board'
  const v = voteLine(vote)
  if (outcome === 'approved') return `${b} approved the project${v ? ' ' + v : ''} at the ${fmtDate(hearingDate)} hearing.${appealEnds ? ` The appeal period ends ${fmtDate(appealEnds)}.` : ''}`
  if (outcome === 'approved_conditions') {
    const c = (conditions || []).filter(Boolean)
    return `${b} approved the project${v ? ' ' + v : ''} at the ${fmtDate(hearingDate)} hearing, with ${c.length === 1 ? 'one condition' : c.length + ' conditions'}: ${c.join('; ')}.${appealEnds ? ` The appeal period ends ${fmtDate(appealEnds)}.` : ''}`
  }
  if (outcome === 'continued') return `${b} continued the hearing${continuedTo ? ' to ' + fmtDate(continuedTo) : ''}.`
  return `${b} did not approve the project at the ${fmtDate(hearingDate)} hearing.`
}

/** The email to the client, sent in the coordinator's name after they read it. */
export function clientEmailDraft({ outcome, projectName, bodyName, agencyCity, hearingDate, vote, conditions, appealEnds, storyPoles, coordinatorName, greeting }) {
  if (!isApproval(outcome)) return null
  const b = bodyName || 'the board'
  const v = voteLine(vote)
  const c = (conditions || []).filter(Boolean)
  const lines = []
  lines.push(greeting ? `${greeting},` : 'Hello,')
  lines.push(`Good news: ${agencyCity ? agencyCity + '’s ' : 'the '}${b} approved ${projectName || 'your project'} on ${fmtDayDate(hearingDate)}${v ? ', ' + v : ''}. Design review is one of the most demanding steps in getting a home approved, and the project is through it. Congratulations.`)
  if (c.length) lines.push(`The approval comes with ${c.length === 1 ? 'one condition' : c.length + ' conditions'}:\n${c.map(x => '- ' + x).join('\n')}`)
  const ns = nextSteps({ outcome, appealEnds, storyPoles, conditions: c })
  if (ns.length) lines.push('What happens next:\n' + ns.map(x => '- ' + x).join('\n'))
  lines.push('You can see all of this on your Client Hub. If you have any questions, reply to this email.')
  lines.push(firstName(coordinatorName) || 'Alder James')
  return {
    subject: `${projectName || 'Your project'}: approved by the ${b}`,
    body: lines.join('\n\n'),
  }
}

/** The thank-you to the board members, one version per outcome, sent in the coordinator's name. */
export function boardEmailDraft({ outcome, projectName, projectAddress, bodyName, hearingDate, continuedTo, coordinatorName }) {
  const b = bodyName || 'the board'
  const what = projectAddress ? `${projectName ? projectName + ' at ' : 'the project at '}${projectAddress.split('\n')[0]}` : (projectName || 'the project')
  const sign = [coordinatorName || '', 'Alder James'].filter(Boolean).join('\n')
  let mid
  if (isApproval(outcome)) mid = `Thank you for the time you gave ${what} at the ${fmtDate(hearingDate)} hearing, and for the care that went into your review. Your comments make the project better, and we will carry them forward faithfully.`
  else if (outcome === 'continued') mid = `Thank you for the time you gave ${what} at the ${fmtDate(hearingDate)} hearing. We appreciate the direction, and we look forward to returning${continuedTo ? ' on ' + fmtDate(continuedTo) : ''}.`
  else mid = `Thank you for the time and attention you gave ${what} at the ${fmtDate(hearingDate)} hearing. We appreciate the board’s careful consideration and will take the comments to heart.`
  return {
    subject: `Thank you: ${projectName || 'our project'}, ${b} hearing ${fmtDate(hearingDate)}`,
    body: [`Dear Members of the ${b},`, mid, 'With appreciation,', sign].join('\n\n'),
  }
}

/** The project channel post. Excited on an approval; plain on anything else. */
export function slackText({ outcome, projectName, bodyName, hearingDate, vote, conditions, appealEnds, storyPoles, coordinatorName, hubPosted, clientEmailed, boardEmailed, conditionThreads }) {
  const b = bodyName || 'the board'
  const v = voteLine(vote)
  const who = coordinatorName || 'The Permitting Coordinator'
  const parts = []
  if (isApproval(outcome)) {
    parts.push(`:tada: *${projectName} was approved by the ${b}${v ? ', ' + v : ''}!* (hearing ${fmtDayDate(hearingDate)})`)
    if (conditions && conditions.length) parts.push(`Conditions:\n${conditions.map(c => '• ' + c).join('\n')}` + (conditionThreads ? `\nEach is a thread riding ${conditionThreads}.` : ''))
    if (appealEnds) parts.push(`Appeal period ends ${fmtDate(appealEnds)}${storyPoles ? '. The story poles stay up until then.' : '.'}`)
    parts.push('Congratulations, team. :clap:')
  } else if (outcome === 'continued') {
    parts.push(`*${projectName}*: the ${b} continued the ${fmtDate(hearingDate)} hearing.`)
  } else {
    parts.push(`*${projectName}*: the ${b} did not approve the project at the ${fmtDate(hearingDate)} hearing.`)
  }
  const sent = []
  if (hubPosted) sent.push('a card on the Client Hub')
  if (clientEmailed) sent.push(`an email to ${clientEmailed}`)
  if (boardEmailed) sent.push(`a thank-you to ${boardEmailed} board member${boardEmailed === 1 ? '' : 's'}`)
  parts.push(`Recorded by ${who}${sent.length ? '. Sent: ' + sent.join(', ') + '.' : '.'}`)
  return parts.join('\n')
}

/** The appeal period ended: the team's post. */
export function appealSlackText({ projectName, bodyName, appealEnds, status, note, storyPoles, coordinatorName, clientNotified }) {
  const b = bodyName || 'the board'
  if (status === 'none') return [`:white_check_mark: *${projectName}*: the appeal period on the ${b} approval ended ${fmtDate(appealEnds)} with no appeals. The approval is final.`,
    storyPoles ? 'The story poles can come down.' : '', `Recorded by ${coordinatorName || 'the Permitting Coordinator'}${clientNotified ? '. The client’s Client Hub card shows it.' : '.'}`].filter(Boolean).join('\n')
  return [`:warning: *${projectName}*: an appeal was filed on the ${b} approval.`, note ? `"${note}"` : '', `Recorded by ${coordinatorName || 'the Permitting Coordinator'}. Nothing has gone to the client.`].filter(Boolean).join('\n')
}
